/**
 * Traduction des mathématiques Typst en LaTeX, pour l'aperçu KaTeX.
 *
 * Les messages rédigés par Claude sont écrits en **Typst** : c'est ce moteur
 * qui compose les bilans PDF (CLAUDE.md §2), et une commande LaTeX y
 * apparaîtrait telle quelle sous les yeux de l'élève. Mais l'interface web
 * compose avec KaTeX, qui ne connaît que le LaTeX : `$151/20$` s'y affichait
 * en toutes lettres.
 *
 * La source enregistrée reste donc du Typst — c'est elle qui part au PDF, elle
 * n'est jamais réécrite. Seul l'affichage est traduit, au vol.
 *
 * Le sous-ensemble couvert est celui que le prompt de `remediation.py` autorise
 * et que les messages produits emploient réellement : fractions `/`, exposants
 * `^(...)`, `sqrt`, symboles nommés, ensembles `NN` `ZZ` `QQ`. Ce qui sort de ce
 * cadre est recopié tel quel plutôt que deviné : mieux vaut un symbole affiché
 * brut qu'une formule fausse.
 */

/** Symboles Typst nommés → commande LaTeX équivalente. */
const SYMBOLES: Record<string, string> = {
  // Ensembles de nombres — omniprésents en seconde.
  NN: "\\mathbb{N}",
  ZZ: "\\mathbb{Z}",
  QQ: "\\mathbb{Q}",
  RR: "\\mathbb{R}",
  CC: "\\mathbb{C}",
  DD: "\\mathbb{D}",

  // Opérations et relations.
  times: "\\times",
  div: "\\div",
  dot: "\\cdot",
  "dot.op": "\\cdot",
  "plus.minus": "\\pm",
  "minus.plus": "\\mp",
  "eq.not": "\\neq",
  "lt.eq": "\\leq",
  "gt.eq": "\\geq",
  approx: "\\approx",
  prop: "\\propto",
  equiv: "\\equiv",

  // Ensembles et logique.
  in: "\\in",
  "in.not": "\\notin",
  subset: "\\subset",
  "subset.eq": "\\subseteq",
  supset: "\\supset",
  "supset.eq": "\\supseteq",
  union: "\\cup",
  sect: "\\cap",
  inter: "\\cap",
  emptyset: "\\emptyset",
  forall: "\\forall",
  exists: "\\exists",
  and: "\\wedge",
  or: "\\vee",
  not: "\\neg",
  infinity: "\\infty",
  oo: "\\infty",

  // Flèches et ponctuation.
  "arrow.r": "\\to",
  "arrow.l": "\\leftarrow",
  "arrow.r.double": "\\Rightarrow",
  "arrow.l.r.double": "\\Leftrightarrow",
  dots: "\\dots",
  "dots.h": "\\dots",
  "dots.v": "\\vdots",
  degree: "^\\circ",
  percent: "\\%",
  euro: "€",

  // Grandes opérations.
  sum: "\\sum",
  product: "\\prod",
  integral: "\\int",
  lim: "\\lim",

  // Fonctions usuelles : Typst les compose droites, LaTeX aussi via \sin…
  sin: "\\sin",
  cos: "\\cos",
  tan: "\\tan",
  log: "\\log",
  ln: "\\ln",
  exp: "\\exp",
  max: "\\max",
  min: "\\min",

  // Alphabet grec.
  alpha: "\\alpha",
  beta: "\\beta",
  gamma: "\\gamma",
  delta: "\\delta",
  epsilon: "\\varepsilon",
  zeta: "\\zeta",
  eta: "\\eta",
  theta: "\\theta",
  iota: "\\iota",
  kappa: "\\kappa",
  lambda: "\\lambda",
  mu: "\\mu",
  nu: "\\nu",
  xi: "\\xi",
  pi: "\\pi",
  rho: "\\rho",
  sigma: "\\sigma",
  tau: "\\tau",
  phi: "\\varphi",
  chi: "\\chi",
  psi: "\\psi",
  omega: "\\omega",
  Gamma: "\\Gamma",
  Delta: "\\Delta",
  Theta: "\\Theta",
  Lambda: "\\Lambda",
  Pi: "\\Pi",
  Sigma: "\\Sigma",
  Phi: "\\Phi",
  Psi: "\\Psi",
  Omega: "\\Omega",
};

/** Opérateurs de deux caractères, à reconnaître avant les simples. */
const OPERATEURS_DOUBLES: Record<string, string> = {
  "<=": "\\leq",
  ">=": "\\geq",
  "!=": "\\neq",
  "->": "\\to",
  "=>": "\\Rightarrow",
  ":=": ":=",
};

/**
 * Fonctions Typst appelées avec des parenthèses, et la forme LaTeX
 * correspondante. Les arguments arrivent déjà traduits.
 */
const FONCTIONS: Record<string, (args: string[]) => string> = {
  sqrt: ([x]) => `\\sqrt{${x}}`,
  root: ([n, x]) => (x === undefined ? `\\sqrt{${n}}` : `\\sqrt[${n}]{${x}}`),
  abs: ([x]) => `\\left|${x}\\right|`,
  norm: ([x]) => `\\left\\|${x}\\right\\|`,
  floor: ([x]) => `\\left\\lfloor ${x}\\right\\rfloor`,
  ceil: ([x]) => `\\left\\lceil ${x}\\right\\rceil`,
  frac: ([a, b]) => `\\frac{${a}}{${b ?? ""}}`,
  binom: ([a, b]) => `\\binom{${a}}{${b ?? ""}}`,
  // `arrow(A B)` : la flèche du vecteur. `vec` compose au contraire un vecteur
  // colonne — la confusion est explicitement signalée à la relecture des
  // messages (`remediation.verifier`), l'aperçu doit donc la montrer telle
  // qu'elle sortira du PDF.
  arrow: ([x]) => `\\overrightarrow{${x}}`,
  vec: (args) => `\\begin{pmatrix}${args.join(" \\\\ ")}\\end{pmatrix}`,
  hat: ([x]) => `\\hat{${x}}`,
  bar: ([x]) => `\\bar{${x}}`,
  tilde: ([x]) => `\\tilde{${x}}`,
  overline: ([x]) => `\\overline{${x}}`,
  underline: ([x]) => `\\underline{${x}}`,
  // `class("opening", "]")` déclare la classe d'un symbole. Les intervalles
  // à la française en vivent : Typst voit `]` fermer, si bien que le `-`
  // qui suit devient une soustraction et que `]-2 ; 7]` se compose
  // « ] − 2 ; 7] ». LaTeX a les mêmes classes, sous d'autres noms.
  // Les arguments arrivent traduits, d'où le dépouillement du `\text{}`.
  class: ([classe, symbole]) => {
    const nu = (jeton: string | undefined) =>
      (jeton ?? "").replace(/^\\text\{([\s\S]*)\}$/, "$1");
    const commandes: Record<string, string> = {
      opening: "mathopen",
      closing: "mathclose",
      fence: "mathopen",
      relation: "mathrel",
      binary: "mathbin",
      punctuation: "mathpunct",
      large: "mathop",
      unary: "mathord",
      normal: "mathord",
      vary: "mathord",
    };
    return `\\${commandes[nu(classe)] ?? "mathord"}{${nu(symbole)}}`;
  },
  bb: ([x]) => `\\mathbb{${x}}`,
  cal: ([x]) => `\\mathcal{${x}}`,
  bold: ([x]) => `\\mathbf{${x}}`,
  upright: ([x]) => `\\mathrm{${x}}`,
  italic: ([x]) => `\\mathit{${x}}`,
};

type Jeton = { type: "nom" | "nombre" | "texte" | "signe"; valeur: string };

/**
 * Un morceau composé, et sa forme sans parenthèses extérieures.
 *
 * Typst efface les parenthèses d'un opérande de fraction : `(n(n+5))/2`
 * s'affiche avec la barre pour seul groupement. Garder les deux écritures
 * évite de reconstruire le morceau au moment où l'on découvre le `/`.
 */
type Morceau = { latex: string; operande: string };

function decouper(source: string): Jeton[] {
  const jetons: Jeton[] = [];
  let i = 0;

  while (i < source.length) {
    const c = source[i];

    if (/\s/.test(c)) {
      i += 1;
      continue;
    }

    // Un nom Typst peut porter des points : `plus.minus`, `arrow.r.double`.
    if (/[A-Za-z]/.test(c)) {
      const suite = /^[A-Za-z][A-Za-z0-9]*(?:\.[A-Za-z][A-Za-z0-9]*)*/.exec(
        source.slice(i),
      )!;
      // Le point d'un nom composé (`plus.minus`) doit être suivi d'une lettre :
      // le point final d'une phrase, lui, reste dehors.
      jetons.push({ type: "nom", valeur: suite[0] });
      i += suite[0].length;
      continue;
    }

    if (/[0-9]/.test(c)) {
      const suite = /^[0-9]+(?:[.,][0-9]+)*/.exec(source.slice(i))!;
      jetons.push({ type: "nombre", valeur: suite[0] });
      i += suite[0].length;
      continue;
    }

    if (c === '"') {
      const fin = source.indexOf('"', i + 1);
      const texte = fin === -1 ? source.slice(i + 1) : source.slice(i + 1, fin);
      jetons.push({ type: "texte", valeur: texte });
      i = fin === -1 ? source.length : fin + 1;
      continue;
    }

    const double = source.slice(i, i + 2);
    if (double in OPERATEURS_DOUBLES) {
      jetons.push({ type: "signe", valeur: double });
      i += 2;
      continue;
    }

    jetons.push({ type: "signe", valeur: c });
    i += 1;
  }

  return jetons;
}

/** Caractères que LaTeX interprète et qu'un texte cité ne doit pas déclencher. */
function echapper(texte: string): string {
  return texte.replace(/([\\{}$&#%_^~])/g, "\\$1");
}

function analyser(jetons: Jeton[]): string {
  let i = 0;

  const suivant = () => (i < jetons.length ? jetons[i] : null);

  /** Un opérande : primaire, plus ses exposants et indices. */
  const atome = (): Morceau => {
    const base = primaire();
    let latex = base.latex;

    while (suivant()?.valeur === "^" || suivant()?.valeur === "_") {
      const signe = jetons[i].valeur;
      i += 1;
      const argument = primaire();
      // `2^(-2)` : les parenthèses groupent, elles ne s'affichent pas.
      latex += `${signe}{${argument.operande}}`;
    }

    // Une fois attaché, le morceau n'est plus « entre parenthèses » : le
    // numérateur de `a^m/a^n` est bien `a^m` tout entier.
    return { latex, operande: latex === base.latex ? base.operande : latex };
  };

  const primaire = (): Morceau => {
    const jeton = suivant();
    if (!jeton) return { latex: "", operande: "" };
    i += 1;

    if (jeton.type === "nombre") {
      // `7,55` : sans accolades, KaTeX espace la virgule comme une ponctuation.
      const latex = jeton.valeur.replace(/,/g, "{,}");
      return { latex, operande: latex };
    }

    if (jeton.type === "texte") {
      const latex = `\\text{${echapper(jeton.valeur)}}`;
      return { latex, operande: latex };
    }

    if (jeton.type === "nom") {
      const nom = jeton.valeur;

      if (nom in FONCTIONS && suivant()?.valeur === "(") {
        i += 1;
        const args = arguments_();
        const latex = FONCTIONS[nom](args);
        return { latex, operande: latex };
      }

      if (nom in SYMBOLES) {
        return { latex: SYMBOLES[nom], operande: SYMBOLES[nom] };
      }

      // Un nom d'une lettre est une variable ; plus long, c'est un mot que
      // Typst compose droit et que LaTeX pencherait lettre à lettre.
      const latex = nom.length === 1 ? nom : `\\mathrm{${nom.replace(/\./g, "")}}`;
      return { latex, operande: latex };
    }

    if (jeton.valeur === "(") {
      const contenu = suite([")"]);
      if (suivant()?.valeur === ")") i += 1;
      return { latex: `\\left(${contenu}\\right)`, operande: contenu };
    }

    if (jeton.valeur in OPERATEURS_DOUBLES) {
      const latex = OPERATEURS_DOUBLES[jeton.valeur];
      return { latex, operande: latex };
    }

    // Les caractères que LaTeX réserve, et qui n'ont pas de sens ici.
    const latex = /[{}#&%_~\\]/.test(jeton.valeur)
      ? `\\${jeton.valeur}`
      : jeton.valeur;
    return { latex, operande: latex };
  };

  /** Arguments d'un appel, séparés par des virgules, jusqu'à la parenthèse. */
  const arguments_ = (): string[] => {
    const args: string[] = [];
    while (suivant() && suivant()!.valeur !== ")") {
      args.push(suite([",", ")"]));
      if (suivant()?.valeur === ",") i += 1;
    }
    if (suivant()?.valeur === ")") i += 1;
    return args;
  };

  /**
   * Une suite d'atomes, jusqu'à l'un des signes d'arrêt.
   *
   * C'est ici qu'est traitée la fraction : en Typst, `/` prend l'atome qui
   * précède et celui qui suit, attachements compris.
   */
  const suite = (arrets: string[]): string => {
    const morceaux: Morceau[] = [];

    while (suivant() && !arrets.includes(suivant()!.valeur)) {
      if (suivant()!.valeur === "/") {
        i += 1;
        const denominateur = atome();
        const numerateur = morceaux.pop() ?? { latex: "", operande: "" };
        const latex = `\\frac{${numerateur.operande}}{${denominateur.operande}}`;
        morceaux.push({ latex, operande: latex });
        continue;
      }
      morceaux.push(atome());
    }

    return morceaux.map((morceau) => morceau.latex).join(" ");
  };

  return suite([]);
}

/** Traduit un fragment de maths Typst (sans les `$`) en LaTeX. */
export function mathsTypstVersLatex(source: string): string {
  return analyser(decouper(source));
}

// Segments mathématiques : `$...$`. Typst emploie les mêmes délimiteurs que
// LaTeX, ce qui rend le repérage identique des deux côtés.
const SEGMENTS = /(\$[^$]*\$)/g;

/** Traduit un texte mêlant prose française et maths Typst. */
export function typstVersLatex(texte: string): string {
  return texte
    .split(SEGMENTS)
    .map((segment, index) =>
      index % 2 === 0 ? segment : `$${mathsTypstVersLatex(segment.slice(1, -1))}$`,
    )
    .join("");
}

/**
 * Le texte porte-t-il au moins une formule ?
 *
 * Sert à n'afficher l'aperçu composé que là où il apprend quelque chose : sous
 * un message de prose pure, il ne ferait que répéter la zone de saisie.
 */
export function contientDesMaths(texte: string): boolean {
  return /\$[^$]+\$/.test(texte);
}
